import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Phone, Star } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/landing/Reveal";
import { SERVICE_AREAS, SERVICE_PAGES, COMPANY } from "@/data";

export default function ServiceAreaPage() {
  const { slug } = useParams();
  const area = SERVICE_AREAS.find((a) => a.slug === slug);
  if (!area) return <Navigate to="/404" replace />;

  return (
    <>
      <Seo title={area.title} description={area.meta} path={`/service-areas/${area.slug}`} />

      <section className="relative bg-navy-dark pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="max-w-[1100px] mx-auto px-5 md:px-12">
          <Reveal>
            <Link to="/service-areas" className="font-body text-gold/80 hover:text-gold text-sm">← All service areas</Link>
            <span className="block mt-5 font-display uppercase tracking-[0.2em] text-gold text-sm font-semibold">{area.stateName}</span>
            <h1 className="mt-3 font-display font-bold uppercase text-white text-4xl md:text-6xl leading-[0.92] tracking-tight max-w-[18ch]">
              {area.h1}
            </h1>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold hover:bg-gold-hover text-navy-dark font-bold uppercase tracking-wide text-sm px-8 py-4 transition-colors duration-200">
                Get a Free Estimate <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a href={`tel:${COMPANY.phoneRaw}`} className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/40 text-white hover:border-gold hover:text-gold font-semibold text-sm px-8 py-4 transition-colors duration-200">
                <Phone size={18} /> Call {COMPANY.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="max-w-[1100px] mx-auto px-5 md:px-12 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <Reveal>
              <p className="font-body text-navy/75 text-lg md:text-xl leading-relaxed">{area.body}</p>
            </Reveal>

            {/* Placeholder real photo */}
            <Reveal delay={0.1}>
              <div className="mt-10 aspect-[16/9] rounded-2xl border-2 border-dashed border-navy/15 bg-cloud flex items-center justify-center text-center p-6" data-testid="area-photo-placeholder">
                <span className="font-body text-navy/40 text-sm">[ Add a real project photo from {area.city} — caption with the neighborhood ]</span>
              </div>
            </Reveal>

            {/* Placeholder real testimonial */}
            <Reveal delay={0.15}>
              <div className="mt-8 rounded-2xl border-2 border-dashed border-navy/15 bg-cloud p-8" data-testid="area-testimonial-placeholder">
                <div className="flex gap-1 mb-3 opacity-40">
                  {Array.from({ length: 5 }).map((_, s) => <Star key={s} size={18} className="text-gold fill-gold" />)}
                </div>
                <span className="font-body text-navy/40 text-sm">[ Add a real customer review from a {area.city} client — replace with a verified Google review ]</span>
              </div>
            </Reveal>

            {/* Services links */}
            <Reveal delay={0.2}>
              <h2 className="mt-12 font-display font-bold uppercase text-navy text-2xl md:text-3xl tracking-tight">
                Our services in {area.city}
              </h2>
              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                {SERVICE_PAGES.map((s) => (
                  <Link key={s.slug} to={`/services/${s.slug}`} className="flex items-center justify-between gap-3 bg-cloud rounded-xl px-5 py-4 hover:bg-navy hover:text-white group transition-colors">
                    <span className="font-body font-semibold text-navy group-hover:text-white">{s.nav}</span>
                    <ArrowRight size={18} className="text-navy/30 group-hover:text-gold" />
                  </Link>
                ))}
              </div>
            </Reveal>
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-28 bg-navy rounded-2xl p-7 text-white">
              <h3 className="font-display font-bold text-2xl">Serving {area.city}, {area.state}</h3>
              <p className="mt-2 font-body text-white/70 text-sm">One licensed, insured team for every trade. Free, no-obligation estimates.</p>
              <Link to="/contact" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold hover:bg-gold-hover text-navy-dark font-bold uppercase tracking-wide text-sm px-6 py-3.5 transition-colors">
                Get a Free Estimate
              </Link>
              <a href={`tel:${COMPANY.phoneRaw}`} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-white/30 text-white font-semibold text-sm px-6 py-3.5 hover:border-gold hover:text-gold transition-colors">
                <Phone size={16} /> {COMPANY.phone}
              </a>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
